import { TransferOrUnloadingRequestParticipantDto } from './transfer-or-unloading-request-participant.dto';
import { TransferOrUnloadingRequestParticipantsFlatDto } from './transfer-or-unloading-request-participants-flat.dto';

// Ruoli dei soggetti coinvolti di una richiesta di trasferimento/scarico
// (una riga di TransferOrUnloadingRequestParticipant per ruolo).
// Il valore coincide con il prefisso dei campi "piatti" del form (es. direzioneSicOpinion).
// L'enum sta qui perché @ellf/common-dto non può importare da @ellf/common-utils (creerebbe un ciclo);
// @ellf/common-utils lo riesporta.
// Per aggiungere un ruolo: valore qui, voce in ROLE_FIELDS (+ campi in
// TransferOrUnloadingRequestParticipantsFlatDto se serve la vista piatta nel form).
export enum TransferOrUnloadingRequestParticipantRole {
  CoordinatoreDiRepartoCedente = 'coordinatoreDiRepartoCedente',
  CoordinatoreDiRepartoAccettante = 'coordinatoreDiRepartoAccettante',
  TecnicoSic = 'tecnicoSic',
  DirezioneSic = 'direzioneSic',
  TechnicalAssessment = 'technicalAssessment',
  DirezioneGestioneLogistica = 'direzioneGestioneLogistica',
}

type ParticipantField = Exclude<
  keyof TransferOrUnloadingRequestParticipantDto,
  'id' | 'transferOrUnloadingRequestId' | 'role' | 'user' | 'userId'
>;

// [suffisso del campo piatto, campo della riga del soggetto]
type FieldMap = ReadonlyArray<readonly [string, ParticipantField]>;

const CONTACT: FieldMap = [
  ['Email', 'email'],
  ['PhoneNumber', 'phoneNumber'],
  ['BadgeNumber', 'badgeNumber'],
  ['Signature', 'signature'],
];

// parere con data ed esito, senza motivazioni
const OPINION: FieldMap = [
  ['OpinionDate', 'opinionDate'],
  ['Opinion', 'opinion'],
  ...CONTACT,
];

// L'utente del ruolo è sempre: <ruolo>Id (userId) e <ruolo> (user)
const ROLE_FIELDS: Record<TransferOrUnloadingRequestParticipantRole, FieldMap> =
  {
    [TransferOrUnloadingRequestParticipantRole.CoordinatoreDiRepartoCedente]:
      CONTACT,
    [TransferOrUnloadingRequestParticipantRole.CoordinatoreDiRepartoAccettante]:
      OPINION,
    [TransferOrUnloadingRequestParticipantRole.TecnicoSic]: [
      ...OPINION,
      ['OpinionReasons', 'opinionReasons'],
    ],
    [TransferOrUnloadingRequestParticipantRole.DirezioneSic]: [
      ...OPINION,
      ['OpinionReasons', 'opinionReasons'],
    ],
    [TransferOrUnloadingRequestParticipantRole.TechnicalAssessment]: [
      ...OPINION,
      ['Notes', 'opinionReasons'],
    ],
    // conferma al posto del parere: data, esito e note della conferma
    [TransferOrUnloadingRequestParticipantRole.DirezioneGestioneLogistica]: [
      ['ConfirmationDate', 'opinionDate'],
      ['Confirmation', 'opinion'],
      ['Notes', 'opinionReasons'],
      ...CONTACT,
    ],
  };

const ROLES = Object.keys(
  ROLE_FIELDS,
) as TransferOrUnloadingRequestParticipantRole[];

/** Riga del soggetto con quel ruolo, se presente */
export function findTouParticipant<T extends { role?: string }>(
  participants: ReadonlyArray<T> | null | undefined,
  role: TransferOrUnloadingRequestParticipantRole,
): T | undefined {
  return participants?.find((a) => a.role === role);
}

/**
 * Da participants[] ai campi piatti (un gruppo per ruolo).
 * I ruoli senza riga hanno i campi a null (utente a undefined).
 */
export function touParticipantsToFlat(
  // accetta DTO, righe parziali ed entity (basta che abbiano `role`)
  participants: ReadonlyArray<{ role?: string }> | null | undefined,
): TransferOrUnloadingRequestParticipantsFlatDto {
  const flat: Record<string, unknown> = {};
  for (const role of ROLES) {
    const participant = findTouParticipant(participants, role) as unknown as
      | Record<string, unknown>
      | undefined;
    flat[`${role}Id`] = participant?.['userId'] ?? undefined;
    flat[role] = participant?.['user'] ?? undefined;
    for (const [suffix, field] of ROLE_FIELDS[role]) {
      flat[`${role}${suffix}`] = participant?.[field] ?? null;
    }
  }
  return flat as unknown as TransferOrUnloadingRequestParticipantsFlatDto;
}

/**
 * Dai campi piatti ad participants[].
 * Produce una riga per ogni ruolo di cui compare almeno un campo in `flat`
 * (anche con tutti i valori vuoti: serve per azzerare una riga esistente);
 * i ruoli di cui non compare nessun campo vengono saltati, così un aggiornamento
 * parziale non tocca gli altri passaggi.
 */
export function touFlatToParticipants(
  flat: Partial<TransferOrUnloadingRequestParticipantsFlatDto>,
): Partial<TransferOrUnloadingRequestParticipantDto>[] {
  const source = flat as Record<string, unknown>;
  const participants: Partial<TransferOrUnloadingRequestParticipantDto>[] = [];
  for (const role of ROLES) {
    const keys = [
      `${role}Id`,
      ...ROLE_FIELDS[role].map(([suffix]) => `${role}${suffix}`),
    ];
    if (!keys.some((k) => k in source)) continue;

    const participant: Record<string, unknown> = { role };
    if (`${role}Id` in source) participant['userId'] = source[`${role}Id`];
    for (const [suffix, field] of ROLE_FIELDS[role]) {
      const key = `${role}${suffix}`;
      if (key in source) participant[field] = source[key];
    }
    participants.push(
      participant as unknown as Partial<TransferOrUnloadingRequestParticipantDto>,
    );
  }
  return participants;
}

/** Nomi dei campi piatti di tutti i ruoli (per toglierli da un oggetto prima di inviarlo) */
export const TOU_PARTICIPANT_FLAT_KEYS: readonly string[] = ROLES.flatMap(
  (role) => [
    `${role}Id`,
    role,
    ...ROLE_FIELDS[role].map(([suffix]) => `${role}${suffix}`),
  ],
);
